"use client";

import { cn } from "@/lib/utils";
import LiveagentBubbleIcon from "@/components/icons/liveagent-bubble-icon";
import LiveagentLogoIcon from "@/components/icons/liveagent-logo-icon";
import { useSessionStorage } from "usehooks-ts";
import AvatarCarrie from "@/assets/avatar-carrie.png";
import Image from "next/image";
import { MdClose } from "react-icons/md";
import { BiSolidNoEntry, BiSolidXCircle } from "react-icons/bi";
import { MouseEventHandler, useEffect, useState } from "react";
import { FaCircle } from "react-icons/fa6";

export default function CustomLiveagentButton({
  variant,
  floating = false,
}: {
  variant: string;
  floating: boolean;
}) {
  const [buttonClickedButChatNotLoaded, setButtonClickedButChatNotLoaded] =
    useState(false);

  const [chatButtonHidden, setChatButtonHidden] = useSessionStorage(
    "chatbotHidden",
    false
  );

  const [chatbotDirty, setChatbotDirty] = useSessionStorage(
    "chatbotDirty",
    false
  );

  const handleOpenChatClick: MouseEventHandler<HTMLButtonElement> = () => {
    setButtonClickedButChatNotLoaded(true);
    setChatbotDirty(true);
    // Start a chat from a custom component
    setTimeout(() => {
      // Run outside the event loop so it doesn't affect state changes
      window.embedded_svc.bootstrapEmbeddedService();
    }, 1);
  };

  const handleChatButtonHideClick: MouseEventHandler<SVGElement> = (event) => {
    event.stopPropagation();
    setChatButtonHidden(true);
  };

  const handleClose: MouseEventHandler<HTMLButtonElement | SVGElement> = (
    event
  ) => {
    event.stopPropagation();
    setChatbotDirty(true);
  };

  const handleMobileClick: MouseEventHandler<HTMLButtonElement> = () => {
    const minimized = document.querySelector(
      ".minimizedContainer.embeddedServiceSidebarMinimizedDefaultUI "
    );

    minimized?.dispatchEvent(new MouseEvent("click"));
  };

  useEffect(() => {
    if (variant === "chatbotWindowLoaded") {
      // Reset state when window is loaded
      setButtonClickedButChatNotLoaded(false);
      setChatbotDirty(true);
    }
  }, [setChatbotDirty, variant]);

  if (
    (buttonClickedButChatNotLoaded &&
      (variant === "chatbotLoaded" || variant === "chatbotLoadedNoAgents")) ||
    variant === "chatbotWindowLoading"
  ) {
    return (
      <button
        className={cn(
          "group relative size-12 cursor-progress items-center justify-center rounded-full border-[#e5e4e3] bg-[#026366] md:size-16 lg:size-20",
          floating && "fixed bottom-6 right-6 z-50 lg:bottom-12 lg:right-12",
          chatButtonHidden ? "hidden" : "flex"
        )}
        title="Chatbot is loading..."
        id="liveagent-loading"
      >
        <LiveagentLogoIcon className="size-[55%]" />
        <IconCloseButton onClick={handleChatButtonHideClick} />
      </button>
    );
  } else if (variant === "chatbotLoaded") {
    return (
      <div
        className={cn(
          "custom-liveagent-button",
          floating && "fixed bottom-6 right-6 z-50 lg:bottom-12 lg:right-12",
          chatButtonHidden ? "hidden" : "flex",
          "transform-gpu items-baseline gap-2"
        )}
        title="Talk with our chatbot now."
      >
        <button
          className={cn(
            "gap-4 rounded-lg border border-transparent bg-[#026366] px-2 py-1 shadow-[0_0_16px_3.33px_#01191959] transition-[color,_background-color,_border-color,_shadow] hover:border-neutral-200 hover:bg-neutral-500 hover:shadow-none focus:border-white focus:bg-neutral-500 focus:shadow-none",
            chatbotDirty ? "hidden" : "hidden md:flex"
          )}
          onClick={handleOpenChatClick}
          id="liveagent-loaded-bubble"
        >
          <div className="flex items-center gap-2 py-1 lg:py-2">
            <Image
              src={AvatarCarrie}
              alt="Carrie, Care Indeed's assistant"
              className="size-10 rounded-full border border-neutral-200"
            />
            <p className="text-subheading-4-400 font-montserrat text-white">
              Hi, how can I help you?
            </p>
          </div>
          <MdClose
            className="cursor-pointer text-white"
            onClick={handleClose}
          />
        </button>
        <button
          className={cn(
            "group relative flex size-12 cursor-pointer items-center justify-center rounded-full border border-[#026366] bg-[#026366] shadow-[0_0_12px_2px_#01191959] transition-all duration-300 ease-out hover:border-[#e5e4e3] hover:bg-neutral-500 hover:shadow-none focus:border-white md:size-16 md:shadow-[0_0_14px_2.67px_#01191959] lg:size-20 lg:shadow-[0_0_16px_3.33px_#01191959]"
          )}
          onClick={handleOpenChatClick}
          id="liveagent-loaded-icon"
        >
          <LiveagentBubbleIcon
            className="size-[55%]"
            state="static"
          />
          <IconCloseButton onClick={handleChatButtonHideClick} />
        </button>
      </div>
    );
  } else if (
    variant === "chatbotWindowLoading" ||
    (buttonClickedButChatNotLoaded &&
      (variant === "chatbotLoaded" || variant === "chatbotLoadedNoAgents"))
  ) {
    return (
      <button
        className={cn(
          "border-[#e5e4e3 flex size-12 cursor-progress items-center justify-center rounded-full bg-[#026366] md:size-16 lg:size-20",
          floating && "fixed bottom-4 right-4 z-50 lg:right-16"
        )}
        title="Chatbot is loading..."
      >
        <LiveagentLogoIcon className="size-[55%]" />
      </button>
    );
  } else if (variant === "chatbotLoadedNoAgents") {
    return (
      <button
        className={cn(
          "group relative size-12 cursor-pointer items-center justify-center rounded-full bg-[#c0bbb9] transition-all duration-300 ease-out md:size-16 lg:size-20",
          floating && "fixed bottom-6 right-6 z-50 lg:bottom-12 lg:right-12",
          chatButtonHidden ? "hidden" : "flex"
        )}
        onClick={handleOpenChatClick}
        id="liveagent-no-agents"
        title="No agents are online."
      >
        <LiveagentBubbleIcon
          className="size-[55%]"
          state="static"
        />
        <BiSolidNoEntry className="absolute right-0 top-0 block size-4 rounded-full bg-white text-[#E00101] group-hover:hidden lg:size-5" />
        <IconCloseButton onClick={handleChatButtonHideClick} />
      </button>
    );
  } else if (variant === "chatbotWindowLoaded") {
    return (
      <button
        className={cn(
          "group relative size-12 cursor-pointer items-center justify-center rounded-full border border-[#026366] bg-[#026366] shadow-[0_0_12px_2px_#01191959] transition-all duration-300 ease-out hover:border-[#e5e4e3] hover:bg-[#5b7f80] hover:shadow-none focus:border-white md:size-16 md:shadow-[0_0_14px_2.67px_#01191959] lg:size-20 lg:shadow-[0_0_16px_3.33px_#01191959]",
          floating && "fixed bottom-6 right-6 z-50 lg:bottom-12 lg:right-12",
          chatButtonHidden ? "hidden" : "flex lg:hidden"
        )}
        title="Reopen minimized chatbot."
        id="liveagent-mobile-min"
        onClick={handleMobileClick}
      >
        <LiveagentBubbleIcon
          className="size-[55%]"
          state="static"
        />
        <FaCircle className="absolute right-0 top-0 block size-4 border-white text-[#16D701] group-hover:hidden lg:size-5" />
        <IconCloseButton onClick={handleChatButtonHideClick} />
      </button>
    );
  } else if (variant === "chatbotUnreadMessage") {
    return (
      <button
        className={cn(
          "group relative size-12 cursor-pointer items-center justify-center rounded-full border border-[#026366] bg-[#026366] shadow-[0_0_12px_2px_#01191959] transition-all duration-300 ease-out hover:border-[#e5e4e3] hover:bg-neutral-500 hover:shadow-none focus:border-white md:size-16 md:shadow-[0_0_14px_2.67px_#01191959] lg:size-20 lg:shadow-[0_0_16px_3.33px_#01191959]",
          floating && "fixed bottom-6 right-6 z-50 lg:bottom-12 lg:right-12",
          chatButtonHidden ? "hidden" : "flex lg:hidden"
        )}
        title="Reopen minimized chatbot."
        id="liveagent-mobile-min-message"
        onClick={handleMobileClick}
      >
        <LiveagentBubbleIcon
          className="size-[55%]"
          state="static"
        />
        <FaCircle className="absolute right-0 top-0 block size-4 border-white text-[#E00101] group-hover:hidden lg:size-5" />
        <IconCloseButton onClick={handleChatButtonHideClick} />
      </button>
    );
  } else if (variant === "initialNoClose") {
    return (
      <button
        className={cn(
          "group relative size-12 cursor-progress items-center justify-center rounded-full bg-[#c0bbb9] transition-all duration-300 ease-out md:size-16 lg:size-20",
          floating && "fixed bottom-6 right-6 z-50 lg:bottom-12 lg:right-12",
          chatButtonHidden ? "hidden" : "flex"
        )}
        title="Chatbot is loading..."
        id="liveagent-initial"
      >
        <LiveagentBubbleIcon
          className="size-[55%]"
          state="loading"
        />
      </button>
    );
  } else {
    return (
      <button
        className={cn(
          "group relative size-12 cursor-progress items-center justify-center rounded-full bg-[#c0bbb9] transition-all duration-300 ease-out md:size-16 lg:size-20",
          floating && "fixed bottom-6 right-6 z-50 lg:bottom-12 lg:right-12",
          chatButtonHidden ? "hidden" : "flex"
        )}
        title="Chatbot is loading..."
        id="liveagent-initial"
      >
        <LiveagentBubbleIcon
          className="size-[55%]"
          state="loading"
        />
        <IconCloseButton onClick={handleChatButtonHideClick} />
      </button>
    );
  }
}

const IconCloseButton = ({
  onClick,
}: {
  onClick: MouseEventHandler<SVGElement>;
}) => {
  return (
    <BiSolidXCircle
      className="absolute right-0 top-0 hidden size-4 cursor-pointer rounded-full bg-white text-black group-hover:block lg:size-5"
      onClick={onClick}
      title="Hide the chat button."
    />
  );
};
